import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { ImagePath } from "../../assests";

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const AdPopup: React.FC<Props> = ({ isOpen, onClose }) => {
  const [isImageDownloaded, setIsImageDownloaded] = useState(false);

  // Preload image
  useEffect(() => {
    if (!isOpen) {
      setIsImageDownloaded(false);
      return;
    }

    let isMounted = true;

    const imgPreloader = new Image();
    imgPreloader.src = ImagePath.AdPopup;

    imgPreloader.onload = () => {
      if (isMounted) {
        setIsImageDownloaded(true);
      }
    };

    imgPreloader.onerror = () => {
      if (isMounted) {
        setIsImageDownloaded(true);
      }
    };

    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  // Prevent background scrolling
  useEffect(() => {
    if (isOpen && isImageDownloaded) {
      document.documentElement.classList.add("overflow-hidden");
      document.body.classList.add("overflow-hidden");
    } else {
      document.documentElement.classList.remove("overflow-hidden");
      document.body.classList.remove("overflow-hidden");
    }

    return () => {
      document.documentElement.classList.remove("overflow-hidden");
      document.body.classList.remove("overflow-hidden");
    };
  }, [isOpen, isImageDownloaded]);

  if (!isOpen || !isImageDownloaded) {
    return null;
  }

  return (
    <div
      onClick={onClose}
      className="
        fixed
        inset-0
        z-50
        flex
        items-center
        justify-center
        p-2
        sm:p-4
        animate-in
        fade-in
        duration-300
      "
      style={{
        backgroundColor: "rgba(47, 94, 46, 0.25)",
        backdropFilter: "blur(4px)",
      }}
    >
      {/* Popup Container */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="
          relative
          w-[96vw]
          max-w-[96vw]
          max-h-[92vh]
          sm:w-auto
          sm:max-w-[85vw]
          sm:max-h-[85vh]
          md:max-w-[70vw]
          md:max-h-[80vh]
          rounded-xl
          sm:rounded-2xl
          overflow-hidden
          shadow-2xl
          animate-in
          zoom-in-95
          duration-300
          flex
          items-center
          justify-center
        "
      >
        {/* Advertisement Image */}
        <img
          src={ImagePath.AdPopup}
          alt="Advertisement"
          decoding="async"
          className="
            block
            w-full
            h-auto
            max-w-full
            max-h-[92vh]
            object-contain
            sm:w-auto
            sm:max-w-[85vw]
            sm:max-h-[85vh]
            md:max-w-[70vw]
            md:max-h-[80vh]
          "
        />

        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close advertisement"
          className="
            absolute
            top-2
            right-2
            sm:top-3
            sm:right-3
            z-50
            flex
            items-center
            justify-center
            rounded-full
            bg-white
            w-8
            h-8
            sm:w-9
            sm:h-9
            md:w-10
            md:h-10
            p-0
            shadow-md
            transition-all
            duration-200
            hover:bg-gray-50
            hover:scale-105
            active:scale-95
          "
        >
          <X
            className="
              w-4
              h-4
              sm:w-5
              sm:h-5
              md:w-5
              md:h-5
              text-iconColor
            "
          />
        </button>
      </div>
    </div>
  );
};

export default AdPopup;